import React, { FC, useState } from 'react';
import { PaginateListMore } from '../../../../../../common/types/common-types';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { ParamsProps } from '../../../../../../common/components/ParamsProvider';
import { getDayStart, getDayEnd } from '../../../../../../common/utils/time-utils';
import PruTable from '../../../../../../common/components/PruTable/PruTable';
import PruFilter, { PruFilterItemType } from '../../../../../../common/components/PruTable/PruFilter';
import { ReportItem, ReportListParams } from '../../../../types/incentive-opening-report-types';
import { fetchIncentiveOpeningReport, fetchReportDownload } from '../../../../network/incentiveOpeningReportCrud';
import { useDataProvider } from 'src/app/common/utils';

type IncentiveOpeningReportListingPageProps = {} & ParamsProps;

const initialState: ReportListParams = {
  requestedDateFrom: getDayStart(new Date()),
  requestedDateTo: getDayEnd(new Date()),
  agentCode: '',
  unitCode: '',
  branchCode: '',
  officeCode: '',
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: Record<string, string>): ReportListParams => {
  return initialParams
    ? {
        requestedDateFrom: initialParams.requestedDateFrom
          ? getDayStart(new Date(initialParams.requestedDateFrom))
          : getDayStart(new Date()),
        requestedDateTo: initialParams.requestedDateTo
          ? getDayEnd(new Date(initialParams.requestedDateTo))
          : getDayEnd(new Date()),
        agentCode: '',
        unitCode: '',
        branchCode: '',
        officeCode: '',
        page: 1,
        limit: 5,
      }
    : initialState;
};

const IncentiveOpeningReportListingPage: FC<IncentiveOpeningReportListingPageProps> = ({ initialParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();
  const defaultFilterValue = paramsInitiator(initialParams);

  const [filterState, setFilterState] = useState<ReportListParams>(defaultFilterValue);
  const [reportList, setReportList] = useState<PaginateListMore<ReportItem>>();

  const { isLoading, refreshData } = useDataProvider<PaginateListMore<ReportItem>>(
    async () => {
      try {
        return await fetchIncentiveOpeningReport(filterState, dispatch);
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const fetchCsvDownload = () => {
    try {
      fetchReportDownload(filterState, dispatch);
    } catch (err) {}
  };

  return (
    <>
      <PruFilter
        title={Translation('report.IncentiveOpening.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'agentCode',
            initialValue: filterState.agentCode,
            displayName: Translation('report.IncentiveOpening.agentCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'unitCode',
            initialValue: filterState.unitCode,
            displayName: Translation('report.IncentiveOpening.unitCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'branchCode',
            initialValue: filterState.branchCode,
            displayName: Translation('report.IncentiveOpening.branchCode'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'officeCode',
            initialValue: filterState.officeCode,
            displayName: Translation('report.IncentiveOpening.officeCode'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'requestedDateFrom',
            fieldTo: 'requestedDateTo',
            initialDateFrom: filterState.requestedDateFrom,
            initialDateTo: filterState.requestedDateTo,
            defaultDateFrom: defaultFilterValue.requestedDateFrom,
            defaultDateTo: defaultFilterValue.requestedDateTo,
            displayName: Translation('report.common.requestDate'),
            required: true,
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            requestedDateFrom: data.requestedDateFrom,
            requestedDateTo: data.requestedDateTo,
            agentCode: data.agentCode,
            unitCode: data.unitCode,
            branchCode: data.branchCode,
            officeCode: data.officeCode,
          })
        }
        fetchData={refreshData}
      />
      <PruTable
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('report.IncentiveOpening.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('report.common.export'),
            onClick: () => fetchCsvDownload(),
          },
        ]}
        operationDef={[]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'agentCode',
            displayName: Translation('report.IncentiveOpening.agentAdCode'),
            renderData: (row) => row.agentCode,
          },
          {
            keyIndex: 'unitCode',
            displayName: Translation('report.IncentiveOpening.unitCode'),
            renderData: (row) => row.unitCode,
          },
          {
            keyIndex: 'branchCode',
            displayName: Translation('report.IncentiveOpening.branchCode'),
            renderData: (row) => row.branchCode,
          },
          {
            keyIndex: 'officeCode',
            displayName: Translation('report.IncentiveOpening.officeCode'),
            renderData: (row) => row.officeCode,
          },
          {
            keyIndex: 'incentiveName',
            displayName: Translation('report.IncentiveOpening.incentiveOpened'),
            renderData: (row) => row.incentiveName,
          },
          {
            keyIndex: 'totalOpen',
            displayName: Translation('report.IncentiveOpening.numberOfOpenings'),
            renderData: (row) => row.totalOpen,
          },
          {
            keyIndex: 'interactionDate',
            displayName: Translation('report.IncentiveOpening.firstOpeningDate'),
            renderData: (row) => row.interactionDate,
          },
        ]}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default IncentiveOpeningReportListingPage;
